import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractsApi } from '../services/api';
import type { CreateContractInput, UpdateContractInput } from '../types';

// File-local query-key factory. Keys are runtime-identical to the previous
// inline arrays, so cache identity and prefix-invalidation are unchanged —
// this only removes the repeated 'contracts' string literal.
//
// Key taxonomy:
//   list keys:              ['contracts', <params-object-or-undefined>]
//   detail keys:            ['contracts', <id-string>]
//   generation-status keys: ['contracts', <id-string>, 'generation-status']
//
// 'contractKeys.lists' is the prefix that matches all list variants but NOT
// detail or generation-status keys (because those have a string id in position
// 1, whereas list keys always have an object or undefined in position 1).
// This allows useUpdateContract / useDeleteContract to invalidate lists without
// nuking the active generation-status poll.
//
// Exported so GenerationStatusBanner can invalidate contractKeys.detail(id)
// itself once polling reaches 'completed' — the banner owns that transition,
// not a mutation hook, so it needs the key shape directly.
export const contractKeys = {
  all: ['contracts'] as const,
  list: (params?: { status?: string; limit?: number }) =>
    ['contracts', params] as const,
  detail: (id: string) => ['contracts', id] as const,
  generationStatus: (id: string) =>
    ['contracts', id, 'generation-status'] as const,
};

export function useContracts(params?: { status?: string; limit?: number }) {
  return useQuery({
    queryKey: contractKeys.list(params),
    queryFn: () => contractsApi.list(params),
  });
}

export function useContract(id: string) {
  return useQuery({
    queryKey: contractKeys.detail(id),
    queryFn: () => contractsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateContractInput) => contractsApi.create(input),
    onSuccess: () => {
      // New contract: invalidate all list variants so every filter view refreshes.
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
  });
}

export function useUpdateContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateContractInput }) =>
      contractsApi.update(id, input),
    onSuccess: (_, variables) => {
      // Invalidate the specific contract detail and all list variants. List keys
      // use an object or undefined in position 1, so prefix-matching
      // contractKeys.all also catches generation-status keys. Use a predicate
      // to restrict invalidation to list and detail keys only, leaving the
      // active generation-status poll undisturbed.
      queryClient.invalidateQueries({
        queryKey: contractKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          // Match ['contracts', <object-or-undefined>] — the list key shape.
          return (
            key[0] === 'contracts' &&
            (key[1] === undefined || (typeof key[1] === 'object' && key[1] !== null && !Array.isArray(key[1]))) &&
            key.length === 2
          );
        },
      });
    },
  });
}

export function useDeleteContract() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => contractsApi.delete(id),
    onSuccess: () => {
      // Invalidate all list variants only — same predicate as useUpdateContract.
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            key[0] === 'contracts' &&
            (key[1] === undefined || (typeof key[1] === 'object' && key[1] !== null && !Array.isArray(key[1]))) &&
            key.length === 2
          );
        },
      });
    },
  });
}

export function useGenerateDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, async = false }: { id: string; async?: boolean }) =>
      contractsApi.generate(id, async),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(variables.id) });
    },
  });
}

export function useDownloadVersion() {
  return useMutation({
    mutationFn: ({ contractId, version }: { contractId: string; version: number }) =>
      contractsApi.downloadVersion(contractId, version),
  });
}

export function useGenerationStatus(id: string, enabled = true) {
  return useQuery({
    queryKey: contractKeys.generationStatus(id),
    queryFn: () => contractsApi.getGenerationStatus(id),
    enabled: !!id && enabled,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === 'completed' || data?.status === 'failed') return false;
      return 5000;
    },
  });
}
